import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiSave, HiX } from "react-icons/hi";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateCountry.scss";

interface CountryFormData {
  name: string;
  currencyId: number;
  countryType: number;
  symbol: string;
  phoneCode: string;
  phoneRegularExpression: string;
  // Warehouse address structure
  warehouseAddressLine1: string;
  warehouseAddressLine2: string;
  warehouseCityName: string;
  warehousePostalCode: string;
  warehousePhone: string;
  isActive: boolean;
  isSuspended: boolean;
}

interface Currency {
  id: number;
  name: string;
  lastExchangeRate: number;
}



export const CreateCountry: React.FC = () => {
  const { t, i18n } = useTranslation(['countries', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState<CountryFormData>({
    name: "",
    currencyId: 0,
    countryType: 0,
    symbol: "",
    phoneCode: "",
    phoneRegularExpression: "",
    warehouseAddressLine1: "",
    warehouseAddressLine2: "",
    warehouseCityName: "",
    warehousePostalCode: "",
    warehousePhone: "",
    isActive: true,
    isSuspended: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<CountryFormData>>({});
  const [currencies, setCurrencies] = useState<Currency[]>([]);


  // Fetch currencies list
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await adminApi.call('/currency', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('countries:messages.fetchError'));
          return;
        }

        setCurrencies(response.data?.items || []);
      } catch (error) {
        showErrorToast(t('countries:messages.fetchError'));
        console.error('Error fetching currencies:', error);
      }
    };

    fetchCurrencies();
  }, []);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "countryType" || name === "currencyId" ? Number(value) : value,
    });

    // Clear error when user starts typing
    if (errors[name as keyof CountryFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CountryFormData> = {};

    // Validate name - extract English value from JSON if present
    let nameValue = formData.name;
    try {
      if (formData.name && formData.name.startsWith('{')) {
        const parsed = JSON.parse(formData.name);
        nameValue = parsed.en || '';
      }
    } catch (e) {
      // Not valid JSON, use as-is
    }

    if (!nameValue.trim()) {
      newErrors.name = t('countries:validation.nameRequired');
    } else if (nameValue.length < 2) {
      newErrors.name = t('countries:validation.nameMinLength');
    } else if (nameValue.length > 100) {
      newErrors.name = t('countries:validation.nameMaxLength');
    }

    if (!formData.symbol.trim()) {
      newErrors.symbol = t('countries:validation.symbolRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await adminApi.call('/country', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('countries:messages.createError'));
        return;
      }

      showSuccessToast(t('countries:messages.createSuccess'));

      // Navigate back to list after successful creation
      setTimeout(() => {
        navigate('/countries');
      }, 2000);

    } catch (error) {
      showErrorToast(t('countries:messages.createError'));
      console.error('Error creating country:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/countries');
  };

  const breadcrumbs = [
    { label: t('countries:breadcrumbs.countries'), path: '/countries' },
    { label: t('countries:breadcrumbs.create') }
  ];

  const headerActions = (
    <div className="header-actions">
      <ActionButton
        variant="secondary"
        icon={HiX}
        onClick={handleCancel}
      >
        {t('common:cancel')}
      </ActionButton>
      <ActionButton
        variant="primary"
        icon={HiSave}
        onClick={handleSubmit}
        loading={loading}
        disabled={loading}
      >
        {t('common:save')}
      </ActionButton>
    </div>
  );

  return (
    <PageLayout
      title={t('countries:createCountry')}
      subtitle={t('countries:subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="create-country-form">
        <form onSubmit={handleSubmit} className="modern-form">
          <div className="form-grid">
            <div className="form-group">
              <MultiLanguageTextInput
                label={t('countries:fields.name')}
                value={formData.name}
                onChange={(value) => {
                  setFormData({ ...formData, name: value });
                  if (errors.name) {
                    setErrors(prev => ({ ...prev, name: undefined }));
                  }
                }}
                placeholder={t('countries:placeholders.name')}
                error={errors.name}
                disabled={loading}
                required
                isRTL={isRTL}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="symbol">
                {t('countries:fields.symbol')} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="symbol"
                name="symbol"
                className={`form-control ${errors.symbol ? 'is-invalid' : ''}`}
                value={formData.symbol}
                onChange={handleInputChange}
                placeholder={t('countries:placeholders.symbol')}
                disabled={loading}
                required
              />
              {errors.symbol && <div className="invalid-feedback">{errors.symbol}</div>}
            </div>

            {/* Phone Code and Regular Expression - Hidden for Source Country (type 2) */}
            {formData.countryType !== 2 && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="phoneCode">
                    {t('countries:fields.phoneCode')}
                  </label>
                  <input
                    type="text"
                    id="phoneCode"
                    name="phoneCode"
                    className={`form-control ${errors.phoneCode ? 'is-invalid' : ''}`}
                    value={formData.phoneCode}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.phoneCode')}
                    disabled={loading}
                  />
                  {errors.phoneCode && <div className="invalid-feedback">{errors.phoneCode}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="phoneRegularExpression">
                    {t('countries:fields.phoneRegularExpression')}
                  </label>
                  <input
                    type="text"
                    id="phoneRegularExpression"
                    name="phoneRegularExpression"
                    className="form-control"
                    value={formData.phoneRegularExpression}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.phoneRegularExpression')}
                    disabled={loading}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="currencyId">
                {t('countries:fields.currency')} <span className="required">*</span>
              </label>
              <select
                id="currencyId"
                name="currencyId"
                className="form-control"
                value={formData.currencyId || ""}
                onChange={handleInputChange}
                disabled={loading}
                required
              >
                <option value="">{t('common:select')}</option>
                {currencies.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.name} ({currency.lastExchangeRate})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="countryType">
                {t('countries:fields.type')} <span className="required">*</span>
              </label>
              <select
                id="countryType"
                name="countryType"
                className="form-control"
                value={formData.countryType}
                onChange={handleInputChange}
                disabled={loading}
                required
              >
                <option value={0}>{t('countries:types.both')}</option>
                <option value={1}>{t('countries:types.destination')}</option>
                <option value={2}>{t('countries:types.source')}</option>
              </select>
            </div>

            {formData.countryType !== 1 && (
              <>
            <div className="form-group">
              <label className="form-label" htmlFor="isActive">
                {t('countries:fields.isActive')}
              </label>
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  className="form-check-input"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="isActive">
                  {formData.isActive ? t('countries:status.active') : t('countries:status.inactive')}
                </label>
              </div>
            </div>
            </>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="isSuspended">
                {t('countries:fields.isSuspended')}
              </label>
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  id="isSuspended"
                  name="isSuspended"
                  className="form-check-input"
                  checked={formData.isSuspended}
                  onChange={(e) => setFormData(prev => ({ ...prev, isSuspended: e.target.checked }))}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="isSuspended">
                  {formData.isSuspended ? t('countries:status.suspended') : t('countries:status.notSuspended')}
                </label>
              </div>
            </div>

            {/* Warehouse Address Section - Hidden for Destination Country (type 1) */}
            {formData.countryType !== 1 && (
              <>
                <div className="form-group full-width">
                  <h4 className="form-section-title">{t('countries:fields.warehouseAddress')}</h4>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="warehouseAddressLine1">
                    {t('countries:fields.warehouseAddressLine1')}
                    <ActionButton
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const input = document.getElementById('warehouseAddressLine1') as HTMLInputElement;
                        if (input) {
                          const start = input.selectionStart || 0;
                          const end = input.selectionEnd || 0;
                          const newValue = formData.warehouseAddressLine1.slice(0, start) + "@customer_id@" + formData.warehouseAddressLine1.slice(end);
                          setFormData({ ...formData, warehouseAddressLine1: newValue });
                          setTimeout(() => {
                            input.focus();
                            input.selectionStart = input.selectionEnd = start + "@customer_id@".length;
                          }, 0);
                        }
                      }}
                      className="ml-2"
                    >
                      customer_id
                    </ActionButton>
                  </label>
                  <input
                    type="text"
                    id="warehouseAddressLine1"
                    name="warehouseAddressLine1"
                    className="form-control"
                    value={formData.warehouseAddressLine1}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.warehouseAddressLine1')}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="warehouseAddressLine2">
                    {t('countries:fields.warehouseAddressLine2')}
                  </label>
                  <input
                    type="text"
                    id="warehouseAddressLine2"
                    name="warehouseAddressLine2"
                    className="form-control"
                    value={formData.warehouseAddressLine2}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.warehouseAddressLine2')}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="warehouseCityName">
                    {t('countries:fields.warehouseCity')}
                  </label>
                  <input
                    type="text"
                    id="warehouseCityName"
                    name="warehouseCityName"
                    className="form-control"
                    value={formData.warehouseCityName}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.warehouseCity')}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="warehousePostalCode">
                    {t('countries:fields.warehousePostalCode')}
                  </label>
                  <input
                    type="text"
                    id="warehousePostalCode"
                    name="warehousePostalCode"
                    className="form-control"
                    value={formData.warehousePostalCode}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.warehousePostalCode')}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="warehousePhone">
                    {t('countries:fields.warehousePhone')}
                  </label>
                  <input
                    type="tel"
                    id="warehousePhone"
                    name="warehousePhone"
                    className="form-control"
                    value={formData.warehousePhone}
                    onChange={handleInputChange}
                    placeholder={t('countries:placeholders.warehousePhone')}
                    disabled={loading}
                  />
                </div>
              </>
            )}


          </div>
        </form>
      </div>
    </PageLayout>
  );
};
